import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { InlineLoading } from "./Spinner";

interface Props {
  requestId: string;
  runId: string | null;
  onClose: () => void;
}

// Shows a historical request_run alongside its response. Reused from any
// place that wants to inspect a past run (the history list in
// RequestBuilder, plus the runner page later).
export default function RunDetailModal({ requestId, runId, onClose }: Props) {
  const q = useQuery({
    queryKey: ["run", requestId, runId],
    queryFn: () => api.requests.run_detail(requestId, runId!),
    enabled: runId != null,
  });
  useEffect(() => {
    if (runId == null) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [runId, onClose]);

  if (runId == null) return null;

  return (
    <div
      className="fixed inset-0 z-[110] grid place-items-center p-4"
      style={{ background: "var(--backdrop)" }}
      onClick={onClose}
    >
      <div
        className="card w-full max-w-5xl h-[min(820px,90vh)] overflow-hidden flex flex-col shadow-[var(--shadow-strong)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-border px-4 py-2.5">
          <h2 className="text-sm font-semibold">Run details</h2>
          {q.data?.run && (
            <span className="text-xs muted-strong">
              {new Date(q.data.run.ran_at).toLocaleString()} · {q.data.run.duration_ms} ms
            </span>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="ml-auto h-8 w-8 grid place-items-center rounded text-text-faint hover:text-text hover:bg-surface-2"
          >×</button>
        </div>
        <div className="flex-1 min-h-0 overflow-auto p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          {!q.data && <div className="col-span-2"><InlineLoading label="Loading run…" /></div>}
          {q.data?.run && (
            <>
              <section className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className={"method-tag " + q.data.run.method.toLowerCase()}>{q.data.run.method}</span>
                  <code className="text-xs font-mono truncate flex-1" title={q.data.run.url}>{q.data.run.url}</code>
                </div>
                <Block label="Request headers">
                  <Headers headers={q.data.run.request_headers} />
                </Block>
                <Block label="Request body">
                  <Body body={q.data.run.request_body} />
                </Block>
              </section>
              <section className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  {q.data.run.status != null && (
                    <span className="chip bg-surface-2 muted-strong mono">{q.data.run.status}</span>
                  )}
                  {q.data.run.error && <span className="chip bg-danger/15 text-danger truncate">{q.data.run.error}</span>}
                  {q.data.run.content_type && (
                    <span className="text-xs muted-strong mono truncate">{q.data.run.content_type}</span>
                  )}
                </div>
                <Block label="Response headers">
                  <Headers headers={q.data.run.response_headers ?? {}} />
                </Block>
                <Block label="Response body">
                  <Body body={q.data.run.response_body} />
                </Block>
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Block({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="label mb-1.5">{label}</div>
      <div className="card p-3 text-xs font-mono whitespace-pre-wrap break-words max-h-72 overflow-auto scroll-pane">
        {children}
      </div>
    </div>
  );
}

function Headers({ headers }: { headers: Record<string, string> }) {
  const entries = Object.entries(headers ?? {});
  if (entries.length === 0) return <span className="muted-strong italic">none</span>;
  return (
    <ul className="space-y-1">
      {entries.map(([k, v]) => (
        <li key={k} className="grid grid-cols-[minmax(120px,1fr)_2fr] gap-2">
          <span className="text-text-faint">{k}</span>
          <span className="break-all">{v}</span>
        </li>
      ))}
    </ul>
  );
}

function Body({ body }: { body: string | null | undefined }) {
  if (!body) return <span className="muted-strong italic">empty</span>;
  return <>{body}</>;
}
